import React from 'react';

// mui components
import { Box, Stack, Typography } from '@mui/material';

// components
import TwAutocomplete from '../../../../components/TwAutocomplete/TwAutocomplete';
import NamedCompanies from './NamesCompanies/NamedCompanies';
import OtherCompanies from './OtherCompanies/OtherCompanies';

// models
import {
  FactoringOption,
  INamedCompanies,
  IOtherCompanies,
  RequiredFields
} from '../../../../redux/api/factoring/factoring.model';

// styles
import './Factoring.scss';

interface FactoringProps {
  factoringCompanies: FactoringOption[];
  factoringCompany: FactoringOption;
  requiredFields: RequiredFields[];
  companyData: INamedCompanies | IOtherCompanies | null;
  sendAllInvoices: boolean;
  loading: boolean;
  handleUpdateFactoringCompany: (
    selectedCompany: FactoringOption,
    data: INamedCompanies | IOtherCompanies | null,
    sendByDefault: boolean
  ) => void;
  hasFactoringError: boolean;
}

const Factoring: React.FC<FactoringProps> = ({
  factoringCompanies,
  factoringCompany,
  companyData,
  sendAllInvoices,
  loading,
  requiredFields,
  handleUpdateFactoringCompany,
  hasFactoringError
}): JSX.Element => {
  const defaultOtherCompanyData: IOtherCompanies = {
    company: '',
    company_email: '',
    company_address: ''
  };

  const defaultNamedCompanyData: INamedCompanies = {
    company: '',
    username: '',
    password: ''
  };

  const handleNamedCompaniesData = (data: INamedCompanies, sendByDefault: boolean): void => {
    handleUpdateFactoringCompany(factoringCompany, data, sendByDefault);
  };

  const handleOtherCompaniesData = (data: IOtherCompanies, sendByDefault: boolean): void => {
    handleUpdateFactoringCompany(factoringCompany, data, sendByDefault);
  };

  const renderDetails = (): React.ReactNode | null => {
    if (factoringCompany.id === 'none') return null;

    if (factoringCompany.id === 'other') {
      const data = companyData ? companyData : defaultOtherCompanyData;

      return (
        <OtherCompanies
          factoringData={data as IOtherCompanies}
          sendAllInvoices={sendAllInvoices}
          handleFactoringData={handleOtherCompaniesData}
        />
      );
    }

    const data = companyData ? companyData : defaultNamedCompanyData;

    return (
      <NamedCompanies
        company={factoringCompany.label}
        sendAllInvoices={sendAllInvoices}
        requiredFields={requiredFields}
        factoringData={data as INamedCompanies}
        handleFactoringData={handleNamedCompaniesData}
      />
    );
  };

  return (
    <Box className="factoring tid-factoring">
      <Typography className="factoring__subtitle tid-factoring__subtitle">Factoring</Typography>
      <Stack direction="column" spacing="0" marginBottom={'8px'}>
        {!hasFactoringError && (
          <>
            <Typography variant="subtitle2" fontWeight="500" color="#7e858e" fontSize="14px">
              Factoring company
            </Typography>

            <div className="tid-factoring__factoring-company">
              <TwAutocomplete
                id="factoringCompany"
                name="factoringCompany"
                optionList={factoringCompanies}
                value={loading ? { id: '', label: 'Loading...', value: '' } : factoringCompany}
                sortList={false}
                disabled={loading}
                error={!factoringCompany}
                onChange={(_, newValue): void => {
                  if (!newValue) return;

                  if (newValue.id === 'none') {
                    handleUpdateFactoringCompany(
                      newValue as FactoringOption,
                      null,
                      sendAllInvoices
                    );
                    return;
                  }

                  if (newValue.id === 'other') {
                    handleUpdateFactoringCompany(
                      newValue as FactoringOption,
                      defaultOtherCompanyData,
                      sendAllInvoices
                    );
                    return;
                  }

                  handleUpdateFactoringCompany(
                    newValue as FactoringOption,
                    defaultNamedCompanyData,
                    sendAllInvoices
                  );
                }}
                enableClearIcon={false}
                disableClearable={true}
              />
            </div>
          </>
        )}

        {hasFactoringError && (
          <Box className="factoring__error">
            <Typography variant="subtitle2" color="error" fontSize="14px" fontWeight={400}>
              Error: Couldn&apos;t get factoring companies
            </Typography>
          </Box>
        )}
        {renderDetails()}
      </Stack>
    </Box>
  );
};

export default Factoring;
